<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import AchievementLevelIcon from "@/components/common/AchievementLevelIcon.vue";
    import { useAchievementLevels } from "@/composables/achievement-levels";
    import { buildPageUrl } from "@/composables/url-utils";
    import { formatAmount, formatWholeAmount } from "@/utils";
    import type { AchievementLevel } from "@/typings/donation";

    // Dashboard "My Miracle Agent Status": progress ring toward the next Miracle Agent level
    // plus the level checklist. Unreached levels link to the donate form prefilled with the
    // amount needed to reach them.
    const { levels, currentAmount, canDonate = false, donateUrl } = defineProps<{
        levels: AchievementLevel[];
        currentAmount: number;
        canDonate?: boolean;
        donateUrl: string;
    }>();

    const { sortedLevels, currentLevel, nextLevel, isTopLevel, progress, amountToReach } =
        useAchievementLevels(() => levels, () => currentAmount);

    const percent = computed(() => Math.floor(progress.value));
    // The ring shows the level already reached; below the first level, that level's icon is faded
    const ringLevel = computed(() => currentLevel.value ?? nextLevel.value);

    const ringLabel = computed(() => {
        const current = currentLevel.value ? `Current level: ${currentLevel.value.displayName}. ` : "";
        if (isTopLevel.value) return `${current}Top Miracle Agent level reached.`;
        if (!nextLevel.value) return "";
        return `${current}${percent.value}% of the way to ${nextLevel.value.displayName}.`;
    });

    // SVG ring: fill animates from empty to the current progress on load
    const ringRadius = 45;
    const ringCircumference = 2 * Math.PI * ringRadius;
    const displayedProgress = ref(0);
    const ringOffset = computed(() => ringCircumference * (1 - displayedProgress.value / 100));
    onMounted(() => requestAnimationFrame(() => { displayedProgress.value = progress.value; }));

    const isReached = (level: AchievementLevel) => currentAmount >= level.threshold;

    const levelLinkAttrs = (level: AchievementLevel) => {
        if (!canDonate || isReached(level)) return {};
        const amount = amountToReach(level);
        return {
            href: buildPageUrl(donateUrl, { amount: amount.toFixed(2) }),
            title: `Donate ${formatAmount(amount)} to reach ${level.displayName}`,
            "data-qa-id": `miracle-agent-level-donate-link-${level.displayName.toLowerCase()}`,
        };
    };
</script>

<template>
    <div v-if="sortedLevels.length"
         :class="['agent-status', { 'agent-status--top': isTopLevel }]"
         data-qa-id="miracle-agent-status">
        <div class="agent-progress" role="img" :aria-label="ringLabel">
            <div class="agent-ring">
                <svg class="agent-ring__svg" viewBox="0 0 100 100" aria-hidden="true">
                    <circle class="agent-ring__track" cx="50" cy="50" :r="ringRadius" />
                    <circle class="agent-ring__fill"
                            cx="50" cy="50" :r="ringRadius"
                            :stroke-dasharray="ringCircumference"
                            :stroke-dashoffset="ringOffset" />
                </svg>
                <div class="agent-ring__center" aria-hidden="true">
                    <AchievementLevelIcon v-if="ringLevel"
                                          :level="ringLevel"
                                          decorative
                                          :class="['agent-ring__icon', { 'agent-ring__icon--not-reached': !currentLevel }]" />
                </div>
            </div>
            <div v-if="nextLevel" class="agent-progress__text" aria-hidden="true">
                <span class="agent-progress__percent">{{ percent }}%</span>
                <span class="agent-progress__caption" data-qa-id="miracle-agent-ring-next-level">
                    to {{ nextLevel.displayName }}
                </span>
            </div>
        </div>

        <ul class="agent-levels">
            <li v-for="level in sortedLevels"
                :key="level.threshold"
                :class="['agent-level', {
                    'agent-level--reached': isReached(level),
                    'agent-level--next': level === nextLevel,
                }]">
                <component :is="levelLinkAttrs(level).href ? 'a' : 'span'" v-bind="levelLinkAttrs(level)" class="agent-level__row">
                    <span class="agent-level__check" aria-hidden="true"></span>
                    <AchievementLevelIcon :level="level" decorative class="agent-level__icon" />
                    <span class="agent-level__name">{{ level.displayName }}</span>
                    <span class="agent-level__amount">{{ formatWholeAmount(level.threshold) }}</span>
                    <span v-if="isReached(level)" class="sr-only">(reached)</span>
                </component>
            </li>
        </ul>
    </div>
</template>

<style scoped lang="scss">
    .agent-status {
        display: flex;
        align-items: center;
        gap: var(--cmnh-size-spacing-6);
        margin: 30px 0;
    }

    // Ring with the level icon, and the progress text under it
    .agent-progress {
        flex: 0 0 110px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--cmnh-size-spacing-2);
    }

    .agent-ring {
        position: relative;
        width: 110px;
        height: 110px;
    }

    .agent-progress__text {
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 1.1;
    }

    .agent-progress__percent {
        color: var(--cmnh-color-dark);
        font-size: 18px;
        font-weight: 700;
    }

    .agent-progress__caption {
        color: var(--cmnh-color-gray-600);
        font-size: 11px;
        font-weight: 600;
    }

    .agent-ring__svg {
        width: 100%;
        height: 100%;
        transform: rotate(-90deg); // start the fill at 12 o'clock
    }

    .agent-ring__track,
    .agent-ring__fill {
        fill: none;
        stroke-width: 6;
    }

    // Unfilled part gently pulses toward blue, timed like the donation flow's current step
    .agent-ring__track {
        stroke: var(--cmnh-color-gray-200);
        animation: agent-track-pulse 2.5s ease-in-out infinite;
    }

    .agent-ring__fill {
        stroke: var(--cmnh-color-primary-base);
        stroke-linecap: round;
        transition: stroke-dashoffset 1s ease-out;
    }

    .agent-ring__center {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .agent-ring__icon {
        width: 80px;
        height: auto;
    }

    // Below the first level: preview it faded, like the checklist's unreached levels
    .agent-ring__icon--not-reached {
        opacity: 0.4;
    }

    // Top level reached: the ring is full and shows the top level's icon
    .agent-status--top .agent-ring__track {
        animation: none;
    }

    // Level checklist
    .agent-levels {
        flex: 1;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .agent-level + .agent-level {
        margin-top: 6px;
    }

    .agent-level__row {
        display: flex;
        align-items: center;
        gap: var(--cmnh-size-spacing-3);
        color: var(--cmnh-color-dark);
        font-size: 13px;
        text-decoration: none;
    }

    a.agent-level__row:hover,
    a.agent-level__row:focus-visible {
        color: var(--cmnh-color-primary-base);

        .agent-level__name {
            text-decoration: underline;
        }
    }

    .agent-level__check {
        flex: 0 0 16px;
        width: 16px;
        height: 16px;
        border: 2px solid var(--cmnh-color-gray-200);
        border-radius: 2px;
        background-color: var(--cmnh-color-white);
        color: var(--cmnh-color-white);
        font-size: 8px;
        line-height: 12px;
        text-align: center;
    }

    .agent-level__icon {
        flex: 0 0 22px;
        width: 22px;
        height: auto;
        opacity: 0.4; // matches the donation flow's future levels
    }

    .agent-level__name {
        flex: 1;
    }

    .agent-level__amount {
        color: var(--cmnh-color-gray-600);
    }

    .agent-level--reached {
        .agent-level__check {
            border-color: var(--cmnh-color-dark);
            background-color: var(--cmnh-color-dark);

            &::before {
                content: "✔";
            }
        }

        .agent-level__icon {
            opacity: 1;
        }
    }

    .agent-level--next {
        font-weight: 600;

        .agent-level__check {
            border-color: var(--cmnh-color-primary-base);
            animation: agent-checkbox-pulse 2.5s infinite;
        }

        .agent-level__icon {
            opacity: 1;
        }
    }

    @keyframes agent-track-pulse {
        0%,
        100% {
            stroke: var(--cmnh-color-gray-200);
        }

        50% {
            stroke: color-mix(in srgb, var(--cmnh-color-light-blue-base) 30%, var(--cmnh-color-gray-200));
        }
    }

    // Same glow as the donation flow's current step, scaled down for a 16px checkbox
    @keyframes agent-checkbox-pulse {
        0%,
        100% {
            box-shadow: 0 0 0 2px color-mix(in srgb, var(--cmnh-color-light-blue-base) 20%, transparent);
        }

        70% {
            box-shadow: 0 0 0 4px color-mix(in srgb, var(--cmnh-color-light-blue-base) 0%, transparent);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .agent-ring__track,
        .agent-level--next .agent-level__check {
            animation: none;
        }

        .agent-ring__fill {
            transition: none;
        }
    }
</style>
